import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import * as types from '../actions/actionTypes';
import ExternalLink from './ExternalLink';
import Heading from './heading/Heading';
import InternalLink from './InternalLink';
import List from './List';
import { SchriftLogoWithSubLine as Logo } from './Logo';

import { media } from './styles/breakpoints';
import WiderrufButton from './buttons/WiderrufButton';

const Footer = () => {
    const StyledFooter = styled.footer`
      background-color: ${props => props.theme.palette.secondary.A100};


      & .stolo-inner-content {
        width: 100%;
        max-width: 100rem;
        margin-right: auto;
        margin-left: auto;
      }

      & .schnoffle-logo-footer {
        margin: 0 0 1em;
      }
    `;

    const StyledInnerFooter = styled.div`
      display: flex;
      justify-content: space-around;
      align-items: flex-start;
      flex-wrap: wrap;
      flex-direction: row;
      font-size: ${props => props.theme.fontSizes[8]};

      & .stolo-footer-item {
        order: 2;
        flex-grow: 1;
        padding: 0 ${props => props.theme.space[9]};
      }

      & .stolo-footer-link {
        color: ${props => props.theme.palette.secondary[700]};
      }

      & .stolo-footer-item-first {
        margin-left: ${props => props.theme.space[6]};
      }

      & .stolo-pd-approved {
        align-self: center;
        margin: ${props => props.theme.space[8]} auto;
        flex-basis: 100px;
        flex-grow: 0;

        & img {
          max-width: 100px;
          max-height: 100px;

          ${media.tweak600up`
                max-width: 70px;
                max-height: 70px;
            `}

        }
      }

      &:last-child {
        order: 3;
        margin: 2em auto;
      }
    `;

    const TeaserText = styled.div`
      margin: ${props => props.theme.space[10]} ${
        props => props.theme.space[8]} ${props => props.theme.space[5]} ${
        props => props.theme.space[10]};
      max-width: ${props => props.theme.space[20]};
      line-height: 1.15;
      order: 1;
      flex-grow: 2;

      ${media.tweak600up`
            max-width: 90%;
        `}
    `;
    const aboutItems = [
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_HASHTAG }}
        >
            <FormattedMessage
                id="LINKTEXT_HASHTAG"
                description="Label for hashtag contest."
                defaultMessage="Nähpaket gewinnen"
            />
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_FAQ_RECOLORING }}
        >
            <FormattedMessage
                id="LINKTEXT_FAQ_RECOLORING"
                description="Label for link text faq recoloring."
                defaultMessage="FAQ Farben"
            />
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_FAQ_SHIPPING }}
        >
            <FormattedMessage
                id="LINKTEXT_FAQ_SHIPPING"
                description="Label for link text shipping."
                defaultMessage="Versand & Lieferzeiten"
            />
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_ABOUT }}
        >
            <FormattedMessage
                id="LINKTEXT_ABOUT"
                description="Label for link text about us."
                defaultMessage="Über uns"
            />
        </InternalLink>,
    ];

    const sewpatItems = [
        <InternalLink
            className="stolo-footer-link"
            to={{
                type: types.NAVIGATED_TO_SEWPAT_VIEW,
                payload: { sewpatId: 'lilly' },
            }}
        >
            Bluse Lilly
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{
                type: types.NAVIGATED_TO_SEWPAT_VIEW,
                payload: { sewpatId: 'mia' },
            }}
        >
            Shirt Mia
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{
                type: types.NAVIGATED_TO_SEWPAT_VIEW,
                payload: { sewpatId: 'moana' },
            }}
        >
            3in1 Moana - Bluse, Tunika & Kleid
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_SEWPAT_GALLERY }}
        >
            <FormattedMessage
                id="LINKTEXT_SEWPAT"
                description="Label for link text sewpats."
                defaultMessage="...alle Schnitte"
            />
        </InternalLink>,
    ];

    const fabricItems = [
        <a
            href="/faq/stoffe#1176"
            className="stolo-footer-link"
        >
            Viskose Jersey
        </a>,
        <a
            href="/faq/stoffe#1175"
            className="stolo-footer-link"
        >
            Baumwollstoff
        </a>,
        <a
            href="/faq/stoffe#1236"
            className="stolo-footer-link"
        >
            Polyestersatin
        </a>,
        <a
            href="/faq/stoffe#1178"
            className="stolo-footer-link"
        >
            Baumwoll Jersey
        </a>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_COLORSWATCHES_CART_FORM }}
        >
            <FormattedMessage
                id="LINKTEXT_COLORSWATCHES_CARTFORM"
                description="Label for link text to color swatches pate."
                defaultMessage="Muster / Farbkarte"
            />
        </InternalLink>,
    ];

    const serviceItems = [
        <ExternalLink
            className="stolo-footer-link"
            href="mailto:info@schnittmuster-berlin.de"
        >Service Schnittmuster</ExternalLink>,
        <ExternalLink
            className="stolo-footer-link"
            href="mailto:wir@schnoffle.de"
        >Service Stoffe</ExternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_NEWSLETTER_FORM }}
        >
            <FormattedMessage
                id="LINKTEXT_NEWSLETTER"
                description="Label for newsletter link text."
                defaultMessage="Newsletter"
            />
        </InternalLink>,
    ];

    const legalItems = [
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_IMPRINT }}
            rel="nofollow"
        >
            <FormattedMessage
                id="LINKTEXT_LEGAL_IMPRINT"
                description="Label for footer link text legal imprint."
                defaultMessage={'{imprint}'}
                values={{
                    imprint:
                        <React.Fragment><span>Impre</span><span>ssum</span></React.Fragment>,
                }}
            />
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_TOS }}
            rel="nofollow"
        >
            <FormattedMessage
                id="LINKTEXT_LEGAL_TOS"
                description="Label for footer link text legal tos."
                defaultMessage={'{tos}'}
                values={{
                    tos:
                        <React.Fragment><span>A</span><span>G</span><span>B</span></React.Fragment>,
                }}
            />
        </InternalLink>,
        <InternalLink
            className="stolo-footer-link"
            to={{ type: types.NAVIGATED_TO_PRIVACY }}
            rel="nofollow"
        >
            <FormattedMessage
                id="LINKTEXT_LEGAL_PRIVACY"
                description="Label for footer link text legal privacy."
                defaultMessage={'{privacy}'}
                values={{
                    privacy:
                        <React.Fragment><span>Dat</span><span>enschu</span><span>tz</span></React.Fragment>,
                }}
            />
        </InternalLink>,
    ];

    const lineheight = 2;

    return (
        <StyledFooter role="contentinfo">
            <StyledInnerFooter className="stolo-inner-content">
                <TeaserText
                    className="stolo-teaser stolo-footer-item-first stolo-footer-item"
                >
                    <Logo className="schnoffle-logo-footer"/>
                    <FormattedMessage
                        id="FOOTER_TEASER_TEXT"
                        description="Text to show in footer for newsletter subcription form."
                        defaultMessage="...ist unser Motto. Bei www.schnoffle.de kaufst Du Designer-Schnitt und Stoff im Näh-Paket. Das Besondere: Du gestaltest die Stoffe selbst. Alles wird nach Deinen Vorgaben hier in Deutschland bedruckt."
                    />
                </TeaserText>
                <div className="stolo-footer-item">
                    <Heading
                        level={2} text={<FormattedMessage
                        id="HEADING_FOOTER_SEWPATS"
                        description="Heading for sewpat links in footer."
                        defaultMessage="Schnittmuster"
                    />}
                    />
                    <List items={sewpatItems} lineheight={lineheight}/>
                </div>
                <div className="stolo-footer-item">
                    <Heading
                        level={2} text={<FormattedMessage
                        id="HEADING_FOOTER_FABRICS"
                        description="Heading for fabrics items in footer"
                        defaultMessage="Stoffqualitäten"
                    />}
                    />
                    <List items={fabricItems} lineheight={lineheight}/>
                </div>
                <div className="stolo-footer-item">
                    <Heading
                        level={2} text={<FormattedMessage
                        id="HEADING_FOOTER_ABOUT"
                        description="Heading for about us item in footer"
                        defaultMessage="Info's"
                    />}
                    />
                    <List items={aboutItems} lineheight={lineheight}/>
                </div>
                <div className="stolo-footer-item">
                    <Heading
                        level={2} text={<FormattedMessage
                        id="HEADING_FOOTER_SERVICE"
                        description="Heading for service items in footer"
                        defaultMessage="Kundenservice"
                    />}
                    />
                    <List items={serviceItems} lineheight={lineheight}/>
                </div>
                <div className="stolo-footer-item">
                    <Heading
                        level={2} text={<FormattedMessage
                        id="HEADING_FOOTER_LEGAL"
                        description="Heading for legal stuff item in footer"
                        defaultMessage="Rechtliches"
                    />}
                    />
                    <WiderrufButton text={
                        <FormattedMessage
                            id="BUTTON_TEXT_FOOTER_WIDERRUF"
                            description="Text for legal button in footer"
                            defaultMessage="Vertrag widerrufen"
                        />

                    } href="https://widerruf.beckerundkarsten.de/?shop=schnoffle"
                    />
                    <List items={legalItems} lineheight={lineheight}/>
                </div>

                <div className="stolo-footer-item stolo-pd-approved">
                    <a href="https://patterndesigns.com"><img
                        src="https://d2k02hhj0n6fcf.cloudfront.net/images/qualitity_pd_grey.png"
                        alt="Patterndesigns.com Logo"
                    /></a>
                </div>
            </StyledInnerFooter>
        </StyledFooter>
    );
};

export default Footer;



// WEBPACK FOOTER //
// ./src/components/Footer.js